import { Node, Edge } from "@xyflow/react";

// Convert backend node type to UI node type
export const getUINodeType = (backendType: string): string => {
    const typeMap: Record<string, string> = {
        "FILE-UPLOAD": "FILE-UPLOAD",
        "FILE-OCR": "FILE-OCR",
        "FILE-PARSE": "FILE-PARSE",
        LLM: "LLM",
        "FILE-DATABASE": "FILE-DATABASE",
        "document-store": "document-store",
        schedule: "schedule",
        conditional: "conditional",
        "compare-data": "compare-data",
        "file-export": "file-export",
        email: "email",
        export: "export",
        TRANSFORM: "TRANSFORM",
        "FILE-COMPARE": "FILE-COMPARE",
        HTTP: "HTTP",
        PDF: "PDF",
        "FOR-EACH": "FOR-EACH",
        "FILE-READ": "FILE-READ",
        "GEMINI-UNDERSTANDING": "GEMINI-UNDERSTANDING",
        "ERROR-CATCHER": "ERROR-CATCHER",
    };
    return typeMap[backendType] || backendType;
};

// Process nodes to unwrap data.input structure from API
export const processImportedNodes = (
    rawNodes: any[],
    onExecuteNode: (id: string) => void,
): Node<any>[] => {
    // Deduplicate nodes by ID - keep the last occurrence (most recent data)
    const seen = new Set<string>();
    const uniqueNodes = rawNodes.filter((node) => {
        if (seen.has(node.id)) return false;
        seen.add(node.id);
        return true;
    });

    return uniqueNodes.map((node) => {
        const baseData = node.data?.inputs
            ? { ...node.data.inputs }
            : { ...node.data };
        delete baseData.inputs;
        delete baseData.outputs;

        // For HTTP nodes, always use "HttpNode" as the name
        const nodeName =
            node.type === "HTTP"
                ? "HttpNode"
                : node.name || baseData.name || node.id;

        return {
            id: node.id,
            type: getUINodeType(node.type),
            position: node.position,
            data: {
                ...baseData,
                // Preserve name from top-level node or from data (but force "HttpNode" for HTTP nodes)
                name: nodeName,
                outputs: node.data?.outputs,
                onExecuteNode: (id: string) => {
                    console.log(`⚡ Executing node ${id} via callback`);
                    onExecuteNode(id);
                },
            },
        };
    });
};

// Inject template ID into HTTP and OneDrive nodes
export const injectTemplateIdToHttpNodes = (
    nodes: Node<any>[],
    templateId: string
): Node<any>[] => {
    localStorage.setItem("template_id", templateId);
    return nodes.map((node) => {
        if (node.type === "HTTP") {
            return {
                ...node,
                data: {
                    ...node.data,
                    name: "HttpNode", // Always set name to HttpNode for HTTP nodes
                    request_id: templateId,
                },
            };
        }
        if (
            node.type === "ONEDRIVE-CONNECTOR" ||
            node.type === "ONEDRIVE-UPLOAD"
        ) {
            return {
                ...node,
                data: {
                    ...node.data,
                    template_id: templateId,
                },
            };
        }
        localStorage.setItem("template_id", templateId);
        return node;
    });
};

export const processInitialEdges = (rawEdges: any[]): Edge[] => {
    return rawEdges
        .filter((edge) => edge.source !== "START" && edge.target !== "END")
        .map((edge, index) => ({
            id: edge.id || `edge-${edge.source}-${edge.target}-${index}`,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle,
            targetHandle: edge.targetHandle,
            animated: edge.animated !== undefined ? edge.animated : false,
            type: edge.type || "deleteButtonEdge",
            data: edge.data,
        }));
};

// Get all ancestor nodes for a given node ID (recursive)
export const getAllAncestorNodes = (
    nodeId: string,
    nodes: Node[],
    edges: Edge[]
): Node[] => {
    const ancestors = new Set<string>();
    const queue = [nodeId];
    const visited = new Set<string>();

    while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (visited.has(currentId)) continue;
        visited.add(currentId);

        // Find all edges pointing to the current node
        const incomingEdges = edges.filter((edge) => edge.target === currentId);

        incomingEdges.forEach((edge) => {
            if (!ancestors.has(edge.source)) {
                ancestors.add(edge.source);
                queue.push(edge.source);
            }
        });
    }

    // Return the actual node objects
    return nodes.filter((node) => ancestors.has(node.id));
};
